import { useState, useEffect, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown, Phone, Sparkles } from 'lucide-react'
import { mainNavItems } from '@constants/routes'

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [expandedMobileDropdown, setExpandedMobileDropdown] = useState<string | null>(null)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled((prev) => {
        const next = window.scrollY > 20
        return prev === next ? prev : next // avoid needless re-renders while scrolling
      })
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setActiveDropdown(null)
    setExpandedMobileDropdown(null)
  }, [location])

  // Toggle mobile dropdown with proper event handling
  const toggleMobileDropdown = useCallback((label: string) => {
    setExpandedMobileDropdown(prev => prev === label ? null : label)
  }, [])

  // Close mobile menu and reset dropdown state
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false)
    setExpandedMobileDropdown(null)
  }, [])

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  return (
    <>
      {/* Main Header */}
      <header
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'shadow-lg' : ''
        }`}
        style={{
          background: isScrolled
            ? 'linear-gradient(135deg, rgba(15, 12, 41, 0.98) 0%, rgba(48, 43, 99, 0.98) 50%, rgba(36, 36, 62, 0.98) 100%)'
            : 'linear-gradient(135deg, rgba(15, 12, 41, 0.85) 0%, rgba(48, 43, 99, 0.85) 50%, rgba(36, 36, 62, 0.85) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: isScrolled ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="container-enterprise">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div
                className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: '0 0 20px rgba(99, 102, 241, 0.5)',
                }}
              >
                <span className="text-white font-bold text-lg lg:text-xl relative z-10" style={{ fontFamily: 'Inter, sans-serif' }}>
                  X
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg lg:text-xl font-bold text-white tracking-tight">EONIXA</span>
                <span className="text-[10px] lg:text-xs text-slate-400 tracking-wider -mt-0.5 hidden sm:block">DYNAMICS</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {mainNavItems.slice(0, 6).map((item) => {
                const hasChildren = item.children.length > 0
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => hasChildren && setActiveDropdown(item.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <Link
                      to={item.href}
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                        isActive(item.href)
                          ? 'text-primary bg-primary/10'
                          : 'text-slate-300 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {item.label}
                      {hasChildren && (
                        <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                      )}
                    </Link>

                    {/* Dropdown */}
                    <AnimatePresence>
                      {hasChildren && activeDropdown === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute left-0 top-full pt-2"
                        >
                          <div className="glass-card-dark rounded-xl shadow-xl py-2 min-w-[220px]">
                            {item.children.map((child) => (
                              <Link
                                key={child.label}
                                to={child.href}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Phone Number - Desktop */}
              <a
                href="tel:+918390101555"
                className="hidden md:flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>+91 8390101555</span>
              </a>

              {/* CTA Button */}
              <Link
                to="/contact"
                className="hidden sm:flex items-center gap-2 px-5 py-2.5 text-white text-sm font-medium rounded-xl transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)',
                }}
              >
                <Sparkles className="w-4 h-4" />
                <span>Get in Touch</span>
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-slate-700 hover:bg-white/5 transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 lg:hidden"
              style={{ background: 'rgba(0, 0, 0, 0.5)' }}
              onClick={closeMobileMenu}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 z-50 h-full w-[320px] shadow-2xl lg:hidden overflow-y-auto"
              style={{
                background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 40%, #24243e 70%, #1a1a2e 100%)',
              }}
            >
              {/* Mobile Menu Header */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center relative overflow-hidden"
                      style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      }}
                    >
                      <span className="text-white font-bold text-lg relative z-10" style={{ fontFamily: 'Inter, sans-serif' }}>
                        X
                      </span>
                    </div>
                    <div>
                      <span className="text-lg font-bold text-white">EONIXA</span>
                    </div>
                  </div>
                  <button
                    onClick={closeMobileMenu}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Phone className="w-4 h-4" />
                  <span>+91 8390101555</span>
                </div>
              </div>

              {/* Mobile Menu Links */}
              <nav className="p-4">
                {mainNavItems.map((item) => {
                  const hasChildren = item.children && item.children.length > 0
                  const isExpanded = expandedMobileDropdown === item.label
                  const isActiveItem = isActive(item.href)

                  return (
                    <div key={item.label}>
                      {hasChildren ? (
                        // Parent item with children - accordion toggle
                        <>
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              toggleMobileDropdown(item.label)
                            }}
                            aria-expanded={isExpanded}
                            aria-controls={`mobile-submenu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`flex items-center justify-between w-full px-4 py-3 text-base font-medium rounded-lg transition-colors text-left ${
                              isActiveItem
                                ? 'text-primary bg-primary/10'
                                : 'text-slate-300 hover:bg-white/5'
                            }`}
                          >
                            <span>{item.label}</span>
                            <ChevronDown
                              className={`w-4 h-4 transition-transform duration-200 ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          </button>

                          {/* Submenu children */}
                          <AnimatePresence initial={false}>
                            {isExpanded && (
                              <motion.div
                                id={`mobile-submenu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                className="overflow-hidden"
                              >
                                <div className="pl-4 mt-1 space-y-1">
                                  {item.children.map((child) => (
                                    <Link
                                      key={child.label}
                                      to={child.href}
                                      onClick={() => setIsMobileMenuOpen(false)}
                                      className={`flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg transition-colors ${
                                        location.pathname === child.href
                                          ? 'text-primary bg-primary/10'
                                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                                      }`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                                      {child.label}
                                    </Link>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        // Simple link without children
                        <Link
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                            isActiveItem
                              ? 'text-primary bg-primary/10'
                              : 'text-slate-300 hover:bg-white/5'
                          }`}
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                  )
                })}
              </nav>

              {/* Mobile Menu Footer */}
              <div className="p-4 border-t border-white/10">
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 text-white font-medium rounded-xl"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  }}
                >
                  <Sparkles className="w-4 h-4" />
                  Get in Touch
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}